import { LoadingOverlay, Select, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { sortByKey } from '@semios/app-platform-common'
import type { TLanguage } from '@semios/app-platform-i18n-core'
import { InfoMessageBox } from 'components/InfoMessageBox/InfoMessageBox'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import { LANGUAGES, translate } from 'i18n/i18n'
import { isEqual } from 'lodash'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { zodResolver } from 'utils/zodResolver'
import { getLanguageLabels } from '../../_utils/getLanguageLabel'
import type { ContactFormType } from '../_utils/formTypesAndStyles'
import { invalidInputClassName } from '../_utils/formTypesAndStyles'
import { getMultiSelectOptions } from '../_utils/getMultiSelectOptions'
import type { NotificationType } from '../_utils/useRelativeNotification'
import { contactValidationSchema } from '../_utils/validationSchemas'
import { ContactAndGroupSubmitButton } from '../components/ContactAndGroupSubmitButton'
import type { Contact } from '../ContactsAndGroupsContext'
import { useGroupsAndContacts } from '../ContactsAndGroupsContext'

export const ModalDrawerCreateOrEditContact = ({
  opened,
  onClose,
  isEdit,
  selectedContact,
  setSelectedContact,
  showRelativeNotification,
}: {
  opened: boolean
  onClose: () => void
  isEdit: boolean
  selectedContact: Contact | null
  setSelectedContact: Dispatch<SetStateAction<Contact | null>>
  showRelativeNotification: ({ type, message }: { type: NotificationType; message: string }) => void
}) => {
  const isWideScreen = useIsWideScreen()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notificationFunctionToUse = isWideScreen ? showRelativeNotification : showNotification
  const defaultLanguage = userDetailsStore.useSelector((s) => s.language as TLanguage)
  const languageLabels = getLanguageLabels()
  const { groups: allGroups, contacts: allContacts, refreshContactsAndGroups } = useGroupsAndContacts()

  const groupOptions = getMultiSelectOptions(
    allGroups.map((group) => ({
      id: group.id,
      name: group.name,
    })),
  )

  const getDefaultFormState = () => ({
    name: selectedContact?.name || '',
    email: selectedContact?.email || '',
    sms: selectedContact?.sms || '',
    groups: selectedContact?.groups
      ? getMultiSelectOptions(
          selectedContact.groups.map((group) => ({
            id: group.id,
            name: group.name,
          })),
        )
      : [],
    language: selectedContact?.language || defaultLanguage,
  })

  const form = useForm<ContactFormType>({
    initialValues: {
      name: '',
      email: '',
      sms: '',
      groups: [],
      language: defaultLanguage,
    },

    validate: zodResolver(contactValidationSchema(allContacts, selectedContact?.id)),
    validateInputOnBlur: true,
  })

  useEffect(() => {
    form.setValues(getDefaultFormState())
  }, [selectedContact, opened])

  const handleOnClose = () => {
    onClose()

    form.reset()

    setSelectedContact(null)
  }

  const handleSubmitContact = async () => {
    const values = form.values
    const { name, email, sms, groups, language } = values

    const contactBody = {
      name,
      email: email?.trim()?.length ? email.toLowerCase().trim() : null,
      sms: sms?.trim()?.length ? sms.trim() : null,
      groups: groups.map((group) => +group.value),
      language,
    }

    const requestBody: routes.ContactsManagement.Request = {}

    const successText = isEdit
      ? translate.phrases.banyanApp('Contact successfully updated')
      : translate.phrases.banyanApp('Contact successfully created')

    const errorText = isEdit
      ? translate.phrases.banyanApp('Failed to update contact')
      : translate.phrases.banyanApp('Failed to create a new contact')

    if (isEdit && selectedContact?.id) {
      requestBody.contactsToUpdate = {
        [selectedContact.id]: contactBody,
      }
    } else {
      requestBody.contactsToCreate = [contactBody]
    }

    setIsSubmitting(true)

    try {
      const response = await apiFetch({
        url: '/contacts-management',
        body: requestBody,
      })

      const contactId = selectedContact?.id ?? Object.keys(response?.data?.contactsToCreate ?? {})[0]

      if (contactId) {
        notificationFunctionToUse({
          type: 'success',
          message: successText,
        })

        refreshContactsAndGroups()

        handleOnClose()
      } else {
        notificationFunctionToUse({
          type: 'error',
          message: errorText,
        })
      }
    } catch (error) {
      notificationFunctionToUse({
        type: 'error',
        message: errorText,
      })
    }

    setIsSubmitting(false)
  }

  const horizontalGap = isWideScreen ? 50 : 10
  const isFormDirty = !isEqual(form.values, getDefaultFormState())

  return (
    <ModalDrawer
      title={
        isEdit
          ? translate.phrases.banyanApp('Contact Details')
          : translate.phrases.banyanApp('Create Contact')
      }
      opened={opened}
      onClose={handleOnClose}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 300}
      size="lg"
    >
      <LoadingOverlay visible={isSubmitting} />

      <div css={{ paddingBottom: 'calc(90px + env(safe-area-inset-bottom))' }}>
        {!isEdit && (
          <div
            css={{
              margin: `15px ${horizontalGap}px`,
            }}
          >
            <InfoMessageBox>
              {translate.phrases.banyanApp('At least one contact method is required')}
            </InfoMessageBox>
          </div>
        )}
        <form onSubmit={form.onSubmit(() => handleSubmitContact())}>
          <div css={{ padding: `15px ${horizontalGap}px` }}>
            <TextInput
              label={translate.phrases.banyanApp('Full Name')}
              {...form.getInputProps('name')}
              classNames={{
                input: form.errors.name ? invalidInputClassName : undefined,
              }}
              name="searchHack" // this is a hack to make an icon stop appearing on safari
              autoComplete="off"
            />
            <TextInput
              mt="lg"
              label={translate.phrases.banyanApp('Email')}
              {...form.getInputProps('email')}
              classNames={{
                input: form.errors.email || form.errors.noContactMethod ? invalidInputClassName : undefined,
              }}
              onBlur={(event) => {
                if (event.target.value !== '') form.clearFieldError('noContactMethod')
              }}
              name="searchHack" // this is a hack to make an icon stop appearing on safari
              autoComplete="off"
            />
            <TextInput
              mt="lg"
              label={translate.phrases.banyanApp('Phone Number')}
              {...form.getInputProps('sms')}
              classNames={{
                input: form.errors.sms || form.errors.noContactMethod ? invalidInputClassName : undefined,
              }}
              onBlur={(event) => {
                if (event.target.value !== '') form.clearFieldError('noContactMethod')
              }}
              name="searchHack" // this is a hack to make an icon stop appearing on safari
              autoComplete="off"
            />
            <Select
              data={LANGUAGES.map((language) => ({ label: languageLabels[language], value: language }))}
              value={form.values.language}
              onChange={(newState: string | null) => {
                if (newState) form.setFieldValue('language', newState as TLanguage)
              }}
              label={translate.phrases.banyanApp('Language')}
              mt="lg"
            />
            <div>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginTop: 20,
                  marginBottom: '3px',
                  fontSize: 16,
                  fontWeight: 500,
                }}
              >
                <div>{translate.phrases.banyanApp('Add Group(s)')}</div>
                <div css={{ marginLeft: 5, fontWeight: 400, fontStyle: 'italic' }}>
                  {translate.phrases.banyanApp('(optional)')}
                </div>
              </div>
              <MultiSelect
                data={groupOptions}
                selectedData={form.getInputProps('groups').value.sort(sortByKey('label'))}
                placeholder=""
                {...form.getInputProps('groups')}
              />
            </div>
          </div>
          <ContactAndGroupSubmitButton
            label={
              isEdit
                ? translate.phrases.banyanApp('Update Contact')
                : translate.phrases.banyanApp('Create Contact')
            }
            disabled={isEdit && !isFormDirty}
          />
        </form>
      </div>
    </ModalDrawer>
  )
}
